import React, { useState } from "react";
import Lottie from "react-lottie";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Custom components
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";

// Icons
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettingsIcon from "@mui/icons-material/Settings";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// Custom components
import CreateChatbotWizard from "layouts/dashboard/components/CreateChatbotWizard/CreateChatbotWizard";

// Animation data
import animationData from "assets/lottie/chatbot.json";
import { useMaterialUIController } from "context";

// Custom styles for the status chips
const StatusChip = styled(Chip)(({ theme, ownerState }) => {
  const { status } = ownerState;
  let backgroundColor;
  let color;

  if (status === "active") {
    backgroundColor = theme.palette.success.light;
    color = theme.palette.success.dark;
  } else if (status === "draft") {
    backgroundColor = theme.palette.grey[300];
    color = theme.palette.grey[700];
  }

  return {
    backgroundColor,
    color,
    "& .MuiChip-label": {
      fontWeight: "bold",
    },
    "& .MuiChip-icon": {
      color,
    },
  };
});

function UserDashboard() {
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const handleOpenTemplateDialog = () => {
    setOpenTemplateDialog(true);
  };

  const handleCloseTemplateDialog = () => {
    setOpenTemplateDialog(false);
  };

  const defaultLottieOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const stats = [
    {
      title: "Total Conversations",
      value: "435",
      change: "+12% from last week",
      icon: (
        <ChatBubbleOutlineOutlinedIcon fontSize="large" sx={{ color: theme.palette.info.main }} />
      ),
      iconBg: theme.functions.rgba(theme.palette.grey[100], 0.1),
    },
    {
      title: "Active Chatbots",
      value: "2",
      change: "+1 from last week",
      icon: <SmartToyOutlinedIcon fontSize="large" sx={{ color: theme.palette.success.main }} />,
      iconBg: theme.functions.rgba(theme.palette.grey[100], 0.1),
    },
    {
      title: "Avg Response Time",
      value: "1.2s",
      change: "-0.3s from last week",
      icon: <TimerOutlinedIcon fontSize="large" sx={{ color: theme.palette.warning.main }} />,
      iconBg: theme.functions.rgba(theme.palette.grey[100], 0.1),
    },
    {
      title: "User Satisfaction",
      value: "94%",
      change: "+2% from last week",
      icon: <TrendingUpIcon fontSize="large" sx={{ color: theme.palette.error.main }} />,
      iconBg: theme.functions.rgba(theme.palette.grey[100], 0.1),
    },
  ];

  const chatbots = [
    {
      name: "Customer Support Bot",
      updated: "2 hours ago",
      conversations: "234 conversations",
      status: "active",
      icon: <SmartToyOutlinedIcon />,
    },
    {
      name: "FAQ Assistant",
      updated: "1 day ago",
      conversations: "45 conversations",
      status: "draft",
      icon: <SmartToyOutlinedIcon />,
    },
    {
      name: "Lead Generation Bot",
      updated: "3 days ago",
      conversations: "156 conversations",
      status: "active",
      icon: <SmartToyOutlinedIcon />,
    },
  ];

  const quickActions = [
    { text: "Create New Chatbot", icon: <AutoAwesomeIcon /> },
    { text: "View All Conversations", icon: <ChatBubbleOutlineOutlinedIcon /> },
    { text: "Account Settings", icon: <SettingsIcon /> },
  ];

  const proTips = [
    { text: "Start with templates", subtext: "Use our pre-built templates for faster setup" },
    { text: "Test thoroughly", subtext: "Use the test chat to refine responses" },
    { text: "Monitor performance", subtext: "Check analytics to improve over time" },
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: "background.default" }}>
      <Card
        sx={{
          mb: 3,
          p: 2,
          position: "relative",
          overflow: "visible",
          background: (theme) =>
            darkMode
              ? `linear-gradient(135deg, ${theme.functions.rgba(theme.palette.custom.main, 0.1)}, ${
                  theme.palette.background.paper
                })`
              : `linear-gradient(135deg, #f5f3ff, #ede9f6)`,
        }}
      >
        <MDBox
          sx={{
            position: "absolute",
            top: -40,
            right: -30,
            zIndex: 0,
          }}
        >
          <Lottie options={defaultLottieOptions} height={250} width={250} />
        </MDBox>
        <CardContent sx={{ position: "relative", zIndex: 1 }}>
          <MDTypography
            variant="caption"
            color="secondary"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            AI Powered
          </MDTypography>
          <MDTypography
            variant="h3"
            fontWeight="bold"
            sx={{
              mt: 1,
              mb: 1,
              maxWidth: "70%",
              fontSize: { xs: "2rem", md: "2.75rem" },
              lineHeight: 1.25,
              fontWeight: 900,
            }}
          >
            Create Your First{" "}
            <MDBox
              component="span"
              sx={(theme) => ({
                background: `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textFillColor: "transparent",
              })}
            >
              AI Chatbot
            </MDBox>
          </MDTypography>
          <MDTypography color="secondary" sx={{ mb: 3, maxWidth: "60%" }}>
            Build intelligent conversations in minutes. Choose from templates, customize with ease,
            and deploy instantly.
          </MDTypography>
          <MDBox sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              endIcon={<ArrowForwardIcon />}
              onClick={handleOpenTemplateDialog}
              sx={{
                background: (theme) =>
                  `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
                color: "#fff",
                borderRadius: "12px",
                px: 4,
                py: 1.75,
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "1rem",
                boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  background: (theme) =>
                    `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 20px 0 rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              Create Chatbot with AI Magic
            </Button>
            <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }} color="secondary">
              <AccessTimeIcon fontSize="small" />
              <MDTypography variant="body2" color="secondary">
                Setup in 2 minutes
              </MDTypography>
            </MDBox>
          </MDBox>
        </CardContent>
      </Card>

      <CreateChatbotWizard open={openTemplateDialog} onClose={handleCloseTemplateDialog} />

      <Grid container spacing={3}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "background.paper",
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <MDBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <MDBox>
                    <MDTypography color="secondary" gutterBottom>
                      {stat.title}
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      {stat.value}
                    </MDTypography>
                    <MDTypography color="secondary" variant="body2">
                      {stat.change}
                    </MDTypography>
                  </MDBox>
                  <Avatar
                    sx={{
                      bgcolor: stat.iconBg,
                      borderRadius: "12px",
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ backgroundColor: "background.paper" }}>
            <CardContent>
              <MDBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <MDTypography variant="h6" fontWeight="bold">
                  Your Chatbots
                </MDTypography>
                <Button size="small">View All</Button>
              </MDBox>
              <List sx={{ p: 0 }}>
                {chatbots.map((bot, index) => (
                  <ListItem
                    key={bot.name}
                    divider={index < chatbots.length - 1}
                    sx={{ px: 0, py: 1.5 }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: "info.light" }}>{bot.icon}</Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <MDTypography variant="subtitle1" fontWeight="bold">
                          {bot.name}
                        </MDTypography>
                      }
                      secondary={
                        <MDTypography color="secondary" variant="body2">
                          {`${bot.updated} • ${bot.conversations}`}
                        </MDTypography>
                      }
                    />
                    <StatusChip
                      label={bot.status.charAt(0).toUpperCase() + bot.status.slice(1)}
                      ownerState={{ status: bot.status }}
                      icon={
                        bot.status === "active" ? (
                          <Icon sx={{ fontSize: "1.25rem!important" }}>play_arrow</Icon>
                        ) : (
                          <Icon sx={{ fontSize: "1.25rem!important" }}>pause</Icon>
                        )
                      }
                      size="small"
                    />
                    <MoreVertIcon sx={{ ml: 2, color: "text.secondary", cursor: "pointer" }} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ backgroundColor: "background.paper" }}>
                <CardContent>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Quick Actions
                  </MDTypography>
                  {quickActions.map((action) => (
                    <Button
                      key={action.text}
                      fullWidth
                      variant="outlined"
                      startIcon={action.icon}
                      sx={{
                        mb: 1,
                        justifyContent: "flex-start",
                        p: 1.5,
                        textTransform: "none",
                        borderRadius: "12px",
                        borderColor: "divider",
                        "& .MuiSvgIcon-root": {
                          color: "info.main",
                        },
                        "&:hover": {
                          backgroundColor: "action.hover",
                          border: "1px solid #fff",
                        },
                      }}
                    >
                      <MDTypography variant="button" fontWeight="regular" color="text">
                        {action.text}
                      </MDTypography>
                    </Button>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ backgroundColor: "background.paper" }}>
                <CardContent>
                  <MDBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <LightbulbOutlinedIcon sx={{ color: "info.main", mr: 1 }} />
                    <MDTypography variant="h6" fontWeight="bold">
                      Pro Tips
                    </MDTypography>
                  </MDBox>
                  {proTips.map((tip, index) => (
                    <Box key={tip.text} sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: "info.main",
                          color: "white.main",
                          fontSize: "0.875rem",
                          mr: 2,
                        }}
                      >
                        {index + 1}
                      </Avatar>
                      <MDBox>
                        <MDTypography fontWeight="bold">{tip.text}</MDTypography>
                        <MDTypography variant="body2" sx={{ opacity: 0.8 }}>
                          {tip.subtext}
                        </MDTypography>
                      </MDBox>
                    </Box>
                  ))}
                  <Button
                    fullWidth
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      borderRadius: "12px",
                      borderColor: "divider",
                      color: "info.main",
                      justifyContent: "center",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        border: "1px solid #fff",
                      },
                    }}
                  >
                    View Tutorial
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UserDashboard;
